import type { ToolDisplayNames } from '../types';
import type { Icon } from '@phosphor-icons/react';
import type { UIMessage } from 'ai';

export type ToolMessages = {
  preparing: string;
  running: string;
  complete: string;
  transition?: string;
};

export type ToolFriendlyNamesMap<TTools extends string = string> = Record<
  TTools,
  string
>;

export type ToolRegistry = {
  displayNames: Record<string, string>;
  icons: Record<string, Icon>;
  messages: Record<string, ToolMessages>;
};

/**
 * Converts a tool_name to a friendly display name by:
 * - Replacing underscores with spaces
 * - Capitalizing the first letter of each word
 * Example: 'markdown_source_v2_search' → 'Markdown Source V2 Search'
 */
export const toolNameToFriendlyName = (toolName: string): string => {
  return toolName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const buildToolFriendlyNamesMap = (
  sources: { tools: { tool_name: string; friendly_name: string | null }[] }[],
  registry?: ToolRegistry,
): ToolFriendlyNamesMap => {
  const map: ToolFriendlyNamesMap = {};
  sources.forEach((source) => {
    source.tools.forEach((tool) => {
      // Fallback order: API friendly_name → registry display name → generated from tool_name
      map[tool.tool_name] =
        tool.friendly_name ||
        registry?.displayNames[tool.tool_name] ||
        toolNameToFriendlyName(tool.tool_name);
    });
  });
  return map;
};

export const getToolDisplayName = (
  toolName: string,
  registry?: ToolRegistry,
  toolFriendlyNames?: ToolFriendlyNamesMap,
  streamedDisplayNames?: ToolDisplayNames,
): string => {
  const toolId = toolName.replace('tool-', '');

  // Check API-provided friendly names first
  if (toolFriendlyNames && toolId in toolFriendlyNames) {
    return toolFriendlyNames[toolId];
  }

  // Check registry
  if (registry && toolId in registry.displayNames) {
    return registry.displayNames[toolId];
  }

  // Backend-streamed name covers tools missing from /sources (e.g. load_capability)
  if (streamedDisplayNames && toolId in streamedDisplayNames) {
    return streamedDisplayNames[toolId];
  }

  return toolNameToFriendlyName(toolId);
};

export const getStreamedToolDisplayNames = (
  message: UIMessage,
): ToolDisplayNames | undefined => {
  const part = message.parts?.find(
    (messagePart) => messagePart.type === 'data-tool_display_names',
  );
  return part && 'data' in part ? (part.data as ToolDisplayNames) : undefined;
};

export const getToolIcon = (
  toolId: string,
  registry?: ToolRegistry,
): Icon | undefined => {
  if (registry && toolId in registry.icons) {
    return registry.icons[toolId];
  }
  return undefined;
};

export const getToolMessages = (
  toolId: string,
  registry?: ToolRegistry,
): ToolMessages | undefined => {
  if (registry && toolId in registry.messages) {
    return registry.messages[toolId];
  }
  return undefined;
};

export const isKnownTool = (
  toolName: string,
  registry?: ToolRegistry,
): boolean => {
  const toolId = toolName.replace('tool-', '');
  return registry ? toolId in registry.displayNames : false;
};

export const getToolState = (toolPart: {
  state: string;
}):
  | 'input-streaming'
  | 'input-available'
  | 'output-available'
  | 'output-error'
  | string => {
  return toolPart.state;
};

export const getCurrentToolMessage = (
  toolId: string,
  isCompleted: boolean,
  isPreparing: boolean,
  registry?: ToolRegistry,
  toolFriendlyNames?: ToolFriendlyNamesMap,
  streamedDisplayNames?: ToolDisplayNames,
): string => {
  const displayName = getToolDisplayName(
    `tool-${toolId}`,
    registry,
    toolFriendlyNames,
    streamedDisplayNames,
  );

  const messages = getToolMessages(toolId, registry);
  if (messages) {
    // Registry messages may include `{name}`: the backend's per-call name
    // (e.g. "Research skill") when streamed, else the tool's display name.
    const name = streamedDisplayNames?.[toolId] ?? displayName;
    const withName = (text: string): string => text.replace('{name}', name);
    if (isCompleted) return withName(messages.complete);
    if (isPreparing) return withName(messages.preparing);
    return withName(messages.running);
  }

  if (isCompleted) return `${displayName} complete`;
  if (isPreparing) return `Preparing ${displayName}`;
  return `Running ${displayName}.`;
};

type ParsedToolMessage = {
  latestToolPart: { type: string; state: string } | null;
  isCompleted: boolean;
  hasTextPart: boolean;
  isPreparing: boolean;
  isLastToolBeforeGeneration: boolean;
  isWaitingForResponse: boolean;
  currentToolId: string | null;
  currentText: string;
};

export const parseToolCallMessage = (
  message: UIMessage,
  registry?: ToolRegistry,
  toolFriendlyNames?: ToolFriendlyNamesMap,
): ParsedToolMessage => {
  let latestToolPart: { type: string; state: string } | null = null;
  let hasTextPart = false;
  let isCompleted = false;

  const messageParts = message.parts;

  messageParts?.forEach((part) => {
    const partType = part.type;
    const isTool = partType.startsWith('tool-');

    if (isTool) {
      const toolPart = part as { type: string; state: string };
      latestToolPart = toolPart;
      const stateType = getToolState(toolPart);
      if (stateType === 'output-available') {
        isCompleted = true;
      } else {
        isCompleted = false;
      }
    }

    hasTextPart ||= partType === 'text';
  });

  const isPreparing = !latestToolPart && !hasTextPart;
  const isLastToolBeforeGeneration = isCompleted && hasTextPart;
  const isWaitingForResponse = isCompleted && !hasTextPart;

  let currentToolId: string | null = null;
  let currentText = '';

  if (isWaitingForResponse) {
    currentToolId = null;
    currentText = 'Analyzing and writing response';
  } else if (latestToolPart) {
    const toolPartType = (latestToolPart as { type: string }).type;
    const toolId = toolPartType.replace('tool-', '');
    currentToolId = toolId;
    currentText = getCurrentToolMessage(
      toolId,
      isCompleted,
      isPreparing,
      registry,
      toolFriendlyNames,
      getStreamedToolDisplayNames(message),
    );
  }

  return {
    latestToolPart,
    isCompleted,
    hasTextPart,
    isPreparing,
    isLastToolBeforeGeneration,
    isWaitingForResponse,
    currentToolId,
    currentText,
  };
};
